import React, { FC } from 'react';

import { LucidTheme, mqMin } from '@lucid/theme';

import Box from '../box/box';
import Card from '../card/card';
import Link from '../link/link';
import Text from '../text/text';
import Image from '../image/image';
import { ComponentImage, ComponentLink } from '../../types/generics';

export interface ResourceCardProps {
  /** The logo for the card. */
  logo: ComponentImage;
  /** The description for the card. */
  text: string;
  /** The link for the card. */
  cta: ComponentLink;
}
export interface ResourceCardsProps {
  /** Changes the bottom padding if followed by a SimpleCTA component. */
  hasSimpleCta?: boolean;
  /** The cards to be shown in the section container. */
  items: ResourceCardProps[];
  /** Changes the background color to `coolGrey[100]` */
  isDarkBackground?: boolean;
}

const ResourceCards: FC<ResourceCardsProps> = ({ hasSimpleCta = false, items, isDarkBackground = false }) => (
  <Box
    css={(theme: LucidTheme) => [
      {
        backgroundColor: isDarkBackground ? theme.colors.coolGrey[100] : theme.colors.coolGrey[0],
        padding: `${theme.space.s64}px 0`
      },
      hasSimpleCta && {
        padding: `${theme.space.s64}px 0 0`
      }
    ]}
  >
    <Box
      as="section"
      css={(theme: LucidTheme) => ({
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gridTemplateRows: 'repeat(2, 1fr)',
        gridGap: theme.space.s24,
        height: '100%',
        [mqMin.tabletSmall]: {
          '& > a:nth-of-type(odd)': {
            justifySelf: 'end'
          }
        },
        [mqMin.tablet]: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '320px',
          gridGap: theme.space.s16
        },
        [mqMin.computer]: {
          gridGap: theme.space.s48
        }
      })}
    >
      {items.map((item, index) => (
        <Link
          key={`cardLink-${index}`}
          aria-label={`Go to ${item.text} page`}
          href={item.cta.linkUrl}
        >
          <Card
            css={{
              height: '100%',
              maxHeight: '240px',
              [mqMin.tabletSmall]: {
                maxWidth: '264px'
              },
              [mqMin.tablet]: {
                maxHeight: '274px'
              }
            }}
            variant="raised"
          >
            <Card.Content
              css={(theme: LucidTheme) => ({
                display: 'flex',
                flexDirection: 'column',
                padding: theme.space.s16,
                height: '100%',
                margin: 'auto 0',
                [mqMin.tablet]: {
                  padding: theme.space.s32,
                }
              })}
            >
              <Image
                css={{
                  width: '100%',
                  maxWidth: '48px',
                  alignSelf: 'center'
                }}
                imageAlt={item.logo.imageAlt || ''}
                imageSrc={item.logo.imageSrc}
              />
              <Text
                css={(theme: LucidTheme) => ({
                  padding: `${theme.space.s32}px 0`,
                  margin: 'auto 0',
                  maxHeight: theme.space.s104,
                  maxWidth: '192px',
                  textOverflow: 'ellipsis',
                  color: theme.colors.coolGrey[100],
                  [mqMin.tabletSmall]: {
                    maxWidth: '100%'
                  },
                  [mqMin.tablet]: {
                    margin: `${theme.space.s32}px 0`,
                    padding: 0
                  }
                })}
                variant="headlineL"
              >
                {item.text}
              </Text>
              <Text
                css={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  margin: 0
                }}
              >
                {item.cta.linkText}
              </Text>
            </Card.Content>
          </Card>
        </Link>
      ))}
    </Box>
  </Box>
);

export { ResourceCards };
export default ResourceCards;
